import AnalyticsService from '@root/core/src/services/analytics-service';
import ErrorReportService from '@root/core/src/services/error-report-service';
import environment from '@root/core/src/utils/environment';
import { getInstance, init } from '@root/vendor/@eppo/js-client-sdk';

let initialized = false;
let configUsable = false;
let initializationPromise = null;
const listeners = [];

function notifyListeners() {
  listeners.forEach((listener) => listener());
  listeners.length = 0;
}

const EppoClientService = {
  initialize() {
    if (initializationPromise) {
      return initializationPromise;
    }

    const sdkKey = environment.eppoJsSdkKey;

    if (!sdkKey) {
      initialized = true;
      notifyListeners();
      return Promise.resolve();
    }

    initializationPromise = init({
      apiKey: sdkKey,
      // AB test assignments are recorded via Root's own findOrCreateAbTestForIdentifierConfiguration
      // endpoint in each scene, not through Eppo's assignment logger.
      assignmentLogger: {
        logAssignment() {},
      },
    }).then(() => {
      initialized = true;
      configUsable = true;
      notifyListeners();
    }).catch((error) => {
      ErrorReportService.reportError({
        error,
        caughtAt: 'EppoClientService.initialize',
      });
      initialized = true;
      notifyListeners();
    });

    return initializationPromise;
  },

  getStringAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
    if (!initialized || !configUsable) {
      return defaultValue;
    }

    try {
      const variant = getInstance().getStringAssignment(flagKey, subjectKey, subjectAttributes, defaultValue);

      AnalyticsService.trackEvent(
        'EPPO',
        flagKey,
        {
          variant,
          identifier: subjectKey,
          ...subjectAttributes,
        }
      );

      return variant;
    } catch (error) {
      ErrorReportService.reportError({
        error,
        caughtAt: 'EppoClientService.getStringAssignment',
      });
      return defaultValue;
    }
  },

  getBooleanAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
    // Same configUsable gap as getStringAssignment (a blocked/failed config fetch can still
    // emit an EPPO event here). Deliberately left ungated: this flag serves unrelated
    // feature flags across Direct, and gating it inside a cookie-banner fix would change
    // other teams' analytics data. Follow-up: OBX-4364 or a successor ticket.
    if (!initialized) {
      return defaultValue;
    }

    try {
      const variant = getInstance().getBooleanAssignment(flagKey, subjectKey, subjectAttributes, defaultValue);

      AnalyticsService.trackEvent(
        'EPPO',
        flagKey,
        {
          variant,
          identifier: subjectKey,
          ...subjectAttributes,
        }
      );

      return variant;
    } catch (error) {
      ErrorReportService.reportError({
        error,
        caughtAt: 'EppoClientService.getBooleanAssignment',
      });
      return defaultValue;
    }
  },

  isInitialized() {
    return initialized;
  },

  isConfigUsable() {
    return configUsable;
  },

  onInitialized(listener) {
    if (initialized) {
      listener();
      return () => {};
    }

    listeners.push(listener);

    return () => {
      const index = listeners.indexOf(listener);

      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  },

};

export default EppoClientService;
